import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { instancesApi, ResourceConfig } from '../api/client'
import { isBusy } from '../lib/instance-status'

export function useInstances(pausePolling = false) {
  return useQuery({
    queryKey: ['instances'],
    queryFn: instancesApi.list,
    // Adaptive polling: when something is mid-transition (provisioning,
    // restarting…) poll fast so status feels live; when everything is settled
    // poll slowly to save battery/requests. Paused entirely during kanban drag
    // (a mid-drag re-render aborts the native HTML5 drag in Chrome & Firefox).
    refetchInterval: (query) => {
      if (pausePolling) return false
      const data = query.state.data as { status?: string }[] | undefined
      const anyBusy = Array.isArray(data) && data.some((i) => isBusy(i.status as any))
      return anyBusy ? 5_000 : 30_000
    },
  })
}

export function useInstance(id: string) {
  return useQuery({
    queryKey: ['instances', id],
    queryFn: () => instancesApi.get(id),
    refetchInterval: (query) => {
      const status = (query.state.data as { status?: string } | undefined)?.status
      return isBusy(status as any) ? 3_000 : 10_000
    },
    enabled: !!id,
  })
}

export function useInstanceCredentials(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ['instances', id, 'credentials'],
    queryFn: () => instancesApi.credentials(id),
    enabled,
    staleTime: Infinity, // credentials don't change
  })
}

export function useCreateInstance() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: instancesApi.create,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['instances'] }),
  })
}

export function useDeleteInstance() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: instancesApi.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['instances'] }),
  })
}

export function useResetAdminPassword(instanceId: string) {
  return useMutation({
    mutationFn: ({ newPassword, confirmPassword }: { newPassword: string; confirmPassword: string }) =>
      instancesApi.resetAdminPassword(instanceId, newPassword, confirmPassword),
  })
}

export function useCloneInstance() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, name, subdomain }: { id: string; name: string; subdomain: string }) =>
      instancesApi.clone(id, { name, subdomain }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['instances'] }),
  })
}

export function useInstanceResources(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ['instances', id, 'resources'],
    queryFn: () => instancesApi.getResources(id),
    enabled,
    staleTime: 30_000,
  })
}

export function useUpdateResources(instanceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { odoo?: ResourceConfig | null; pg?: ResourceConfig | null }) =>
      instancesApi.updateResources(instanceId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instances', instanceId, 'resources'] })
    },
  })
}

export function useTemplateJobs(instanceId: string, poll: boolean) {
  return useQuery({
    queryKey: ['instances', instanceId, 'template-jobs'],
    queryFn: () => instancesApi.listTemplateJobs(instanceId),
    enabled: !!instanceId,
    refetchInterval: poll ? 5_000 : false,
  })
}

export function useLifecycle() {
  const queryClient = useQueryClient()
  const invalidate = (id: string) => queryClient.invalidateQueries({ queryKey: ['instances', id] })
  return {
    start:   useMutation({ mutationFn: instancesApi.start,   onSuccess: (_, id) => invalidate(id) }),
    stop:    useMutation({ mutationFn: instancesApi.stop,    onSuccess: (_, id) => invalidate(id) }),
    restart: useMutation({ mutationFn: instancesApi.restart, onSuccess: (_, id) => invalidate(id) }),
    retry:   useMutation({ mutationFn: instancesApi.retry,   onSuccess: (_, id) => invalidate(id) }),
    recreate: useMutation({ mutationFn: instancesApi.recreate, onSuccess: (_, id) => invalidate(id) }),
  }
}
